import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { lowBatteryThreshold, lowLqiSignalThreshold } from '@common';
import { NO_VALUE } from '@pipes';
import { UIService } from '@services';
import {
  CommissioningStore,
  DevicesStore,
  GatewayDevice,
  GatewayStore,
  InstallationFeaturesStore,
  RemoteDeviceType,
} from '@store';
import { PillType } from '../../../shared/components/pill/pill.model';
import {
  BatteryProperties,
  DcSensorValueProperties,
  DeviceConnectionStatus,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsStringValueProperty,
  SignalProperties,
  TemperatureProperty,
} from '@vi-iot/client';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { FeatureTileIconVariant } from '../../../shared/components/feature-tile/feature-tile-variants';

import { InstallationService } from '../../services/installation.service';
import { Device } from '../../single-room-control/src-overlay/commissioning/commissioning.model';
import { CommissioningService } from '../../single-room-control/src-overlay/commissioning/commissioning.service';
import { DEVICES_TO_COMMISSIONING_IN_VICARE_THERMOSTAT_MODE } from '../../single-room-control/src-overlay/commissioning/commissioning.model';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';
import { HeatingCircuitHelper } from '../heating-circuit-features-panel/heating-circuit.helper';
@Component({
  selector: 'app-vicare-thermostat',
  templateUrl: './vicare-thermostat.component.html',
  styleUrls: ['./vicare-thermostat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VicareThermostatComponent implements OnInit, OnDestroy {
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  climateSensor: GatewayDevice;
  activeProgram: string;
  gatewaySerial: string;
  targetTemperature$: Observable<Feature<TemperatureProperty, never>>;
  temperature$: Observable<Feature<DcSensorValueProperties, never>>;
  remoteControlDeviceSensorTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  battery$: Observable<Feature<BatteryProperties, never>>;
  wifiLqi$: Observable<Feature<SignalProperties, never>>;
  status$: Observable<Feature<DeviceConnectionStatus, never>>;
  readonly primaryCircuitId = '0';
  readonly heatingOperatingProgramsSchema = HeatingCircuitHelper.HeatingOperatingProgramsSchema;
  readonly noValue = NO_VALUE;
  readonly lowLqiSignalThreshold = lowLqiSignalThreshold;
  readonly lowBatteryThreshold = lowBatteryThreshold;
  readonly iconVariant = FeatureTileIconVariant;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly pillType = PillType;

  get deviceLabel(): string | null {
    if (this.remoteControllerDevices?.length) {
      return '+INSTALLATIONS.INSTALLATION.VICARE_THERMOSTAT.DEVICE_LABEL.VITOTROL_100E';
    }
    if (this.climateSensor) {
      return '+SRC.DEVICE.SMART_CS_GENERIC';
    }

    return null;
  }

  private devicesToCommissioning: Device[] = DEVICES_TO_COMMISSIONING_IN_VICARE_THERMOSTAT_MODE;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  remoteControllerDevices = [];

  constructor(
    public installationService: InstallationService,
    public gatewayStore: GatewayStore,
    public devicesStore: DevicesStore,
    private uiService: UIService,
    private commissioningStore: CommissioningStore,
    private commissioningService: CommissioningService,
    private cdr: ChangeDetectorRef,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.initActivatedRouteSubscription();
    combineLatest([
      this.gatewayStore.climateSensorDevice$,
      this.gatewayStore.getVitotrolDevices$,
      this.devicesStore.selectedDevice$,
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([dev, remote, selectedDevice]) => {
        this.gatewaySerial = selectedDevice.gatewaySerial;
        this.climateSensor = dev;
        this.remoteControllerDevices = remote;
        if (this.climateSensor || this.remoteControllerDevices?.length) {
          this.devicesToCommissioning = DEVICES_TO_COMMISSIONING_IN_VICARE_THERMOSTAT_MODE.filter(
            (value) =>
              value.type !== RemoteDeviceType.CS && value.type !== RemoteDeviceType.VITOTROL_100E
          );
          this.initFeatures();
        } else {
          this.devicesToCommissioning = DEVICES_TO_COMMISSIONING_IN_VICARE_THERMOSTAT_MODE;
        }
        this.commissioningService.devicesToCommissioning$.next(this.devicesToCommissioning);
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  onAddComponent(): void {
    this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.ViCareThermostat);

    this.uiService.activeOverlay$
      .pipe(
        take(1),
        tap(() => {
          this.commissioningService.devicesToCommissioning$.next(this.devicesToCommissioning);
          this.commissioningService.selectedDevice$.next(this.devicesToCommissioning[0]);
        })
      )
      .subscribe((activeOverlay) => {
        if (activeOverlay === FeaturesDetailsOverlayTypeEnum.ViCareThermostat) {
          this.commissioningService.openAddDeviceDialog();
        }
      });
  }

  private initFeatures(): void {
    this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsStringValueProperty, never>(
        FeatureNames.circuitActiveProgram(this.primaryCircuitId)
      )
      .pipe(
        map((feature) => feature?.properties?.value?.value),
        filter((activeProgram) => !!activeProgram)
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((activeProgram: string) => {
        this.activeProgram = activeProgram;
        this.targetTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
          TemperatureProperty,
          never
        >(FeatureNames.circuitOperatingProgram(this.primaryCircuitId, activeProgram));
      });

    if (this.climateSensor) {
      this.temperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        FeatureNames.deviceTemperatureSensor,
        this.climateSensor.id
      );
      this.battery$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        FeatureNames.devicePowerBattery,
        this.climateSensor.id
      );
      this.wifiLqi$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        FeatureNames.deviceZigbeeLqi,
        this.climateSensor.id
      );
      this.status$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        FeatureNames.deviceConnection,
        this.climateSensor.id
      );
    }
    if (this.remoteControllerDevices?.length) {
      this.remoteControlDeviceSensorTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        FeatureNames.deviceTemperatureSensor,
        this.remoteControllerDevices[0].device.id
      );
      this.battery$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        FeatureNames.devicePowerBattery,
        this.remoteControllerDevices[0].device.id,
        this.gatewaySerial
      );
    }
  }

  private initActivatedRouteSubscription(): void {
    this.commissioningStore.listFeatures();
  }
}
